// ============================================================
// A BURST ON THE PLATE (plan body-map H2; the bench's burst, ruled
// 2026-10-09). What an attack's hits play, in time: the hits a dodge or a
// parry cancelled glance off first (a tracer past the outline, or sparks off
// a hand), then each landed hit strikes its own place in turn, 0.15 s apart,
// up to six; the rest land as one rattle over their places, with a count. A
// hit a force field stopped keeps its turn and plays the block. The whole
// figure is thrown once per attack, from the first hit that landed.
//
// Pure. Zero React dependency. Seconds from the burst's start.
// ============================================================

import type { DamageCode, Impact, ImpactHit } from '../impacts';
import { hitSeconds, type PlateHitPlay } from './hits';
import { seeded } from './hash';
import { middleOf, type PlateFrame } from './frame';

/** One hit of the burst as the plate plays it: when, and what stopped or soaked it. */
export interface BurstHit extends PlateHitPlay {
  at: number;
  /** A force field stopped it: the block plays, not the hit. */
  blocked: boolean;
  /** That block burned the field out. */
  overloaded: boolean;
  /** Daemonic protection soaked some of it. */
  soaked: boolean;
}

/** A hit the defence cancelled, glancing off on one side. */
export interface Glance {
  at: number;
  side: 'left' | 'right';
}

export interface Burst {
  glances: Glance[];
  /** The hits shown one by one, in turn. */
  hits: BurstHit[];
  /** Past six, the rest at once: where they land, and when. */
  rattle: { at: number; places: string[] } | null;
  /** A melee attack's glances are parries (sparks off a hand); a shot's are dodges (a tracer past). */
  melee: boolean;
  /** The weapon's special rules (engine/plate/specials plays them over the hits). */
  specials: readonly string[];
  /** Looks a weapon rule lent (plan armoury): played with the specials, never read as one (`bypassOf`). */
  looks?: readonly string[];
}

/** The burst's beat, and how many hits are shown one by one. */
const BURST = { gap: 0.15, shown: 6 };
/** How long a glance and the rattle's count stay; the throw and a critical's strobe. */
const SECONDS = { glance: 0.9, rattle: 1.4, throw: 0.7, strobe: 1.26 };

type BurstImpact = Pick<Impact, 'damageType' | 'dodged' | 'melee'> & {
  hits: readonly ImpactHit[];
  specials?: readonly string[];
  looks?: readonly string[];
};

/**
 * An impact's burst on a body with these locations. A hit's type is its own or the attack's (Impact when neither
 * says); a hit at a place this body has not (or none) is left out - except on a crowd (`locations` null), which has
 * no places: every hit counts, at `horde`.
 */
export function burstOf(impact: BurstImpact, locations: readonly string[] | null): Burst {
  const glances = Array.from({ length: impact.dodged }, (_, k): Glance => ({
    at: k * BURST.gap,
    side: k % 2 ? 'left' : 'right',
  }));
  const landed = locations
    ? impact.hits.filter((h) => h.location && locations.includes(h.location))
    : impact.hits.map((h) => ({ ...h, location: 'horde' }));
  const hits = landed.slice(0, BURST.shown).map((h, i): BurstHit => ({
    location: h.location!,
    type: h.damageType ?? impact.damageType ?? 'I',
    crit: h.crit,
    at: (impact.dodged + i) * BURST.gap,
    blocked: h.blocked,
    overloaded: h.overloaded,
    soaked: h.soaked,
  }));
  const rest = landed.slice(BURST.shown).map((h) => h.location!);
  const rattle = rest.length ? { at: (impact.dodged + BURST.shown) * BURST.gap, places: rest } : null;
  return {
    glances,
    hits,
    rattle,
    melee: impact.melee,
    specials: impact.specials ?? [],
    ...(impact.looks?.length ? { looks: impact.looks } : {}),
  };
}

/** The hits that landed (a hit a field stopped plays its block, nothing else). */
export const landed = (hits: readonly BurstHit[]) => hits.filter((h) => !h.blocked);

/** The burst's first hit that landed, or null: the throw, a vehicle's wave and its facing's flare come with it. */
export const firstLanded = (b: { hits: readonly BurstHit[] }) => b.hits.find((h) => !h.blocked) ?? null;

/** The hits begun by `t`. */
export const begun = (hits: readonly BurstHit[], t: number) => hits.filter((h) => h.at <= t + 1e-9);

/** The latest hit begun at a place by `t`, or null. */
export function latestAt(hits: readonly BurstHit[], location: string, t: number): BurstHit | null {
  return (
    begun(hits, t)
      .filter((h) => h.location === location)
      .at(-1) ?? null
  );
}

/** The latest hit of a type begun by `t` (its filter's fade runs from it), or null. */
export function latestOfType(hits: readonly BurstHit[], type: DamageCode, t: number): BurstHit | null {
  return (
    begun(hits, t)
      .filter((h) => h.type === type && !h.blocked)
      .at(-1) ?? null
  );
}

/**
 * The throw, once per attack: from the first hit that landed, if any landed hit was Explosive or a critical
 * (doubled for a critical). Null: the figure stays where it is.
 */
export function thrower(b: Burst, sturdy = false): { at: number; amp: number } | null {
  const hits = landed(b.hits);
  if (!hits.some((h) => h.type === 'X' || h.crit)) return null;
  // A Sturdy body barely moves for it (the bench's 0.3; plan design-gaps, P5).
  return { at: hits[0].at, amp: (hits.some((h) => h.crit) ? 2 : 1) * (sturdy ? STURDY_KNOCK : 1) };
}

/** How much of the knock a Sturdy body takes. */
const STURDY_KNOCK = 0.3;

/** Seconds until the last of a burst is over: its glances, its hits, the rattle, the throw. */
export function burstSpan(b: Burst): number {
  const throwAt = thrower(b);
  return Math.max(
    0,
    ...b.glances.map((g) => g.at + SECONDS.glance),
    ...b.hits.map((h) => h.at + hitSeconds(h.type, h.crit)),
    ...b.hits.filter((h) => h.crit).map((h) => h.at + SECONDS.strobe),
    b.rattle ? b.rattle.at + SECONDS.rattle : 0,
    throwAt ? throwAt.at + SECONDS.throw : 0,
  );
}

/** Where a plate's glances and rattle are drawn: its drawing's box, its locations' anchors, its scale. */
type BurstFrame = Pick<PlateFrame, 'box' | 'anchors' | 'k'>;

/** A dodged shot: a slanted streak whipping past the outline on its side. */
export interface TracerMark {
  kind: 'tracer';
  x: number;
  y: number;
  /** The streak's run, from its start. */
  dx: number;
  dy: number;
}

/** A parried blow: shards flying off beside a hand, and a star. */
export interface ParryMark {
  kind: 'parry';
  x: number;
  y: number;
  /** Each shard's angle (radians) and length. */
  shards: { a: number; l: number }[];
}

/** Where glance `k` of burst `id` is drawn on this frame: a tracer past the outline, or sparks off a hand. */
export function glanceMark(g: Glance, k: number, id: string, melee: boolean, f: BurstFrame): TracerMark | ParryMark {
  const r = seeded(`${id}:g${k}`);
  const right = g.side === 'right';
  if (melee) {
    const [hx, hy] = f.anchors[right ? 'rightArm' : 'leftArm'] ?? middleOf(f);
    const shards = Array.from({ length: 9 }, (_, i) => ({
      a: (i / 9) * Math.PI * 2 + 0.4 * r(`a${i}`),
      l: (120 + 160 * r(`l${i}`)) * f.k,
    }));
    return { kind: 'parry', x: hx + (right ? -1 : 1) * 160 * f.k, y: hy + 120 * f.k, shards };
  }
  const [x0, y0, x1, y1] = f.box;
  const off = (110 + 120 * r('x')) * f.k;
  const a = (right ? 1 : -1) * 0.35;
  return {
    kind: 'tracer',
    x: right ? x1 + off : x0 - off,
    y: y0 + (y1 - y0) * (0.15 + 0.45 * r('y')),
    dx: Math.sin(a) * 620 * f.k,
    dy: Math.cos(a) * 620 * f.k,
  };
}

/** The rattle: a small star over each place its hits land (jittered), and where its count stands. */
export function rattleMarks(places: readonly string[], id: string, f: BurstFrame) {
  const r = seeded(`${id}:rattle`);
  const stars = places.map((place, i) => {
    const [x, y] = f.anchors[place] ?? middleOf(f);
    return { x: x + (r(`x${i}`) - 0.5) * 220 * f.k, y: y + (r(`y${i}`) - 0.5) * 220 * f.k, delay: i * 0.045 };
  });
  return { stars, count: { x: f.box[2] + 60 * f.k, y: f.box[1] + (f.box[3] - f.box[1]) * 0.3 } };
}
